import { LLMConfig, ChatMessage } from '../../../types/llm'
import { LLMProviderInterface } from '../llm-service'

export class MistralProvider implements LLMProviderInterface {
  private config: LLMConfig
  private baseUrl: string

  constructor(config: LLMConfig) {
    this.config = config
    this.baseUrl = config.baseUrl || 'https://api.mistral.ai/v1'
  }

  async chat(messages: ChatMessage[], config?: Partial<LLMConfig>): Promise<string> {
    const response = await fetch(`${this.baseUrl}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.config.apiKey}`
      },
      body: JSON.stringify({
        model: config?.model || this.config.model,
        messages: messages.map(m => ({
          role: m.role,
          content: m.content
        })),
        temperature: config?.temperature || this.config.temperature || 0.7,
        max_tokens: config?.maxTokens || this.config.maxTokens || 2000
      })
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Mistral API error: ${error}`)
    }

    const data = await response.json() as any
    return data.choices[0].message.content
  }

  async streamChat(
    messages: ChatMessage[],
    config?: Partial<LLMConfig>,
    callbacks?: {
      onToken?: (token: string) => void
      onComplete?: (fullText: string) => void
      onError?: (error: Error) => void
    }
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`
        },
        body: JSON.stringify({
          model: config?.model || this.config.model,
          messages: messages.map(m => ({
            role: m.role,
            content: m.content
          })),
          temperature: config?.temperature || this.config.temperature || 0.7,
          max_tokens: config?.maxTokens || this.config.maxTokens || 2000,
          stream: true
        })
      })

      if (!response.ok) {
        const error = await response.text()
        throw new Error(`Mistral API error: ${error}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let fullText = ''

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') continue

            try {
              const parsed = JSON.parse(data)
              const content = parsed.choices[0]?.delta?.content
              if (content) {
                fullText += content
                callbacks?.onToken?.(content)
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }

      callbacks?.onComplete?.(fullText)
    } catch (error) {
      callbacks?.onError?.(error as Error)
      throw error
    }
  }
}
